import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { DaumPostCodeModal } from '../../../../components/custom/modal/popup/DaumPostCodeModal';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

export const SellTractorSurveyLocationPage = () => {
  const navigate = useNavigate();
  const [location, setLocation] = useState<string>();

  const [isShowDaumPostCodeModal, setIsShowDaumPostCodeModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);

  useEffect(() => {
    setLocation(userSellTractorSurveySelector.location);
  }, [userSellTractorSurveySelector.location]);

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'매물 판매 지역을 알려주세요'}
        description={'입력 된 지역과 판매 위치가 다르다면\n주소 찾기를 하시고 매물 지역을 알려주세요'}
        canConfirm={location !== undefined}
        confirmButtonText={'완료'}
        confirmHandler={() => {
          if (location) {
            dispatch(userSellTractorSurveyActions.changeLocation(location));
          }

          navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true });
        }}
        closeHandler={() => {
          navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true });
        }}
      >
        <SurveyForm padding={'0px 16px'}>
          <RegionSection>
            <RegionHeader>
              <RegionHeaderText>{'입력 된 지역'}</RegionHeaderText>
            </RegionHeader>
            <RegionContent>
              {location === undefined ? (
                <>
                  <Icon name="ExclamationMark" width={20} height={20} />
                  <RegionInfoText>{'아직 위치 정보가 없습니다.'}</RegionInfoText>
                </>
              ) : (
                <>
                  <IconWrapper>
                    <Icon name="RedCheck2" width={13} height={10} />
                  </IconWrapper>
                  <RegionInfoText>{location}</RegionInfoText>
                </>
              )}
            </RegionContent>
          </RegionSection>

          <DaumPostCodeButton onClick={() => setIsShowDaumPostCodeModal(true)}>
            <Icon name="BlueMarker" width={20} height={20} />
            <DaumPostCodeButtonText>{'주소 찾기'}</DaumPostCodeButtonText>
          </DaumPostCodeButton>
        </SurveyForm>
      </SellTractorSurveyPageWrapper>

      <DaumPostCodeModal
        isShow={isShowDaumPostCodeModal}
        onClose={() => setIsShowDaumPostCodeModal(false)}
        onSearchFinished={(searchedRegion: string) => setLocation(searchedRegion)}
      />
    </>
  );
};

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const RegionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
`;

const RegionHeader = styled.div`
  display: flex;
  height: 22px;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
`;

const RegionHeaderText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const RegionContent = styled.div`
  display: flex;
  height: 40px;
  padding: var(--radius-large, 16px);
  align-items: center;
  gap: var(--radius-medium, 8px);
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey30, #d5d5e3);
`;

const RegionInfoText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const DaumPostCodeButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  align-self: stretch;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const DaumPostCodeButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const IconWrapper = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
  min-height: 20px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 5px 3px 5px 4px;
`;
